import { apiFetch } from './client';
import type { SiteDoc, SiteShot } from '../shared/siteContent';
import type { DiscountSync, EffectivePrice, Invoice, PlatformAuditEntry, ScheduledChange } from './admin';

/**
 * When a staff plan change takes hold, and when it starts costing — mirrors
 * the server's `PlanChangeEffective`.
 *
 * `now_bill_at_renewal` is the one that separates the two: the plan is live
 * today, nothing is charged or credited, and the existing renewal bills the
 * new price in full.
 */
export type PlanChangeEffective = 'renewal' | 'now' | 'now_bill_at_renewal';
import type {
  AccountTrial, Plan, PlanFeatures, PlanLimits, PlanSummary, PlanSyncReport, SubscriptionStatus,
} from '../shared/types';

/**
 * The plan catalogue, and its Stripe half.
 *
 * Separate from api/admin.ts because these are the service owner's own
 * records rather than one customer's. Prices are pushed, never synced the
 * other way: `syncPlans` reads Stripe and reports, `pushPlan` is the only
 * call that writes there.
 */

/** What the editor posts. The annual price is derived server-side, never sent. */
export interface PlanBody {
  name: string;
  tagline: string;
  public: boolean;
  popular: boolean;
  trialable: boolean;
  priceMonthlyCents: number | null;
  annualDiscountPct: number;
  currency: string;
  limits: PlanLimits;
  features: PlanFeatures;
}

/** How the Stripe connection looks from the plans list. */
export interface PlansStripeMeta {
  stripeMode: 'live' | 'test' | null;
  webhookUrl: string;
  webhookHealthy: boolean | null;
}

/**
 * The `stripe` block the plans list carries, exactly as the server sends it.
 * Kept separate from PlansStripeMeta because the wire names are the server's
 * and the render names are the page's — `plansStripeMeta` is the one place
 * that translates, so a page never reads a field the route does not send.
 */
export interface PlansStripeBlock {
  connected: boolean;
  mode: 'live' | 'test' | null;
  webhookUrl: string;
  /** true = event within 48h · false = silent 48h+ · 'unknown' = never any */
  receivingEvents: boolean | 'unknown';
  lastEventAt: string | null;
}

/** Wire shape → what the plan pages render. 'unknown' becomes "no events yet". */
export const plansStripeMeta = (s?: PlansStripeBlock): PlansStripeMeta => ({
  stripeMode: s?.connected ? s.mode : null,
  webhookUrl: s?.webhookUrl ?? '',
  webhookHealthy: !s || s.receivingEvents === 'unknown' ? null : s.receivingEvents,
});

/** An account as the plan editor's rail lists it. */
export interface PlanAccountRef {
  id: number;
  name: string;
  billing_interval?: 'month' | 'year';
  subscription_status?: SubscriptionStatus | null;
}

export const listPlansApi = () =>
  apiFetch<{ success: boolean; plans: PlanSummary[]; stripe?: PlansStripeBlock }>('/admin/platform/plans');

/**
 * The whole ordered list of ids, not a move-one-step call — see the route.
 * `sort_order` is also the upgrade ladder, so this changes which plans count
 * as an upgrade from which, not just the order the cards appear in.
 */
export const reorderPlansApi = (ids: number[]) =>
  apiFetch<{ success: boolean; plans: PlanSummary[] }>('/admin/platform/plans/order', {
    method: 'PUT', body: JSON.stringify({ ids }),
  });

export const getPlanApi = (id: number) =>
  apiFetch<{ success: boolean; plan: Plan; accounts: PlanAccountRef[] }>(
    `/admin/platform/plans/${id}`
  );

export const createPlanApi = (body: PlanBody & { key: string }) =>
  apiFetch<{ success: boolean; plan: Plan }>('/admin/platform/plans', {
    method: 'POST', body: JSON.stringify(body),
  });

export const savePlanApi = (id: number, body: PlanBody) =>
  apiFetch<{ success: boolean; plan: Plan }>(`/admin/platform/plans/${id}`, {
    method: 'PATCH', body: JSON.stringify(body),
  });

/** Refused with a 409 naming the subscribers while any account is still on it. */
export const retirePlan = (id: number, retired: boolean) =>
  apiFetch<{ success: boolean; plan: Plan }>(`/admin/platform/plans/${id}/retire`, {
    method: 'POST', body: JSON.stringify({ retired }),
  });

/** How existing subscribers move when a changed price is pushed. */
export type PriceMigration = 'grandfather' | 'at_renewal' | 'now_prorated';

/**
 * Create the Product if missing, mint new Prices for any changed amount and
 * archive the superseded ones. The only call that writes to Stripe.
 */
export const pushPlan = (id: number, migration: PriceMigration) =>
  apiFetch<{ success: boolean; plan: Plan }>(`/admin/platform/plans/${id}/push`, {
    method: 'POST', body: JSON.stringify({ migration }),
  });

/**
 * Forget a plan's Stripe ids so the next push builds the Product afresh.
 * The recovery path when the Product behind them was deleted in Stripe.
 */
export const clearPlanStripeIds = (id: number) =>
  apiFetch<{ success: boolean; plan: Plan }>(`/admin/platform/plans/${id}/stripe-ids/clear`, {
    method: 'POST',
  });

/** Reads Stripe and reports differences; never writes. */
export const syncPlans = () =>
  apiFetch<{
    success: boolean;
    connected: boolean;
    mode: 'live' | 'test' | null;
    /** empty when Stripe is not connected — there is nothing to reconcile */
    report: PlanSyncReport['plans'];
  }>('/admin/platform/plans/sync');

// ---- one account's billing (frame 1d) ----

export interface AccountBillingAccount {
  id: number;
  name: string;
  slug: string;
  status: string;
  plan_id: number;
  billing_interval: 'month' | 'year';
  billing_email: string | null;
  subscription_status: SubscriptionStatus | null;
  current_period_end: string | null;
  grace_until: string | null;
  credit_days: number | null;
  credit_reason: string | null;
  credit_issued_at: string | null;
  price_override_cents: number | null;
  price_override_note: string | null;
  has_stripe_customer: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  /** "customer since" in the page head (R12) */
  created_at: string;
  /**
   * The running percent-off (D4, R12), beside the agreed price and the credit.
   * `discount_months` null with a percent is forever; `discount_ends_at` null
   * with a percent is forever too. All null when there is none.
   */
  discount_percent: number | null;
  /** 1, 3, 6 or 12 from the old percent modal; any number of months from the Change plan dialog (PR 2) */
  discount_months: number | null;
  discount_note: string | null;
  discount_starts_at: string | null;
  discount_ends_at: string | null;
  /** PR 1: set while the account waits out its 30 days before deletion */
  delete_after?: string | null;
}

/**
 * Terms that start later (PR 2, 076): what the account moves onto at its
 * renewal, or when its free period converts. The Change plan dialog opens on
 * them; the Billing tab says when they begin.
 */
export interface PendingTerms {
  id: number;
  starts_at: string;
  plan_id: number;
  plan_key: string | null;
  plan_name: string | null;
  billing_interval: 'month' | 'year';
  price_mode: 'list' | 'discount' | 'agreed';
  discount_percent: number | null;
  discount_months: number | null;
  /** an until-date, as its instant (noon UTC on the day picked) */
  discount_until: string | null;
  /** the percent's own note — internal */
  discount_note: string | null;
  agreed_cents: number | null;
  agreed_note: string | null;
}

/** How long a percent-off runs; `null` beside it means forever. */
export type PercentMonths = 1 | 3 | 6 | 12;

export interface AccountBillingView {
  success: boolean;
  /** what the signed-in staff member may give away (Platform › Rules); null for a Super Admin */
  compLimits?: { freeDays: number; percent: number; agreedFloorPercent?: number } | null;
  account: AccountBillingAccount;
  plan: Plan;
  price: EffectivePrice;
  trial: AccountTrial | null;
  trialHistory: AccountTrial[];
  /** the plans still on sale — retired ones are left out */
  plans: Plan[];
  /**
   * The standing period's restore point (its `previous_plan_id` row), sent
   * beside `plans` because it may be a retired, grandfathered plan the list
   * leaves out — so a revoke can still name its price. Null when there is no
   * standing period or no recorded restore point; absent from an older server.
   */
  restorePlan?: Plan | null;
  usage: {
    forms: number; users: number; submissions_this_month: number;
    storage_bytes: number; submission_records: number;
  };
  invoices: Invoice[];
  stripe: {
    connected: boolean;
    mode: 'live' | 'test' | null;
    /** read from Stripe — a Customer exists for every account, a card does not */
    hasPaymentMethod: boolean;
    customerUrl: string | null;
    lifetimePaidCents: number;
    nextInvoice: { amount_cents: number; date: string } | null;
    /** the move Stripe is holding for the next renewal, read live */
    scheduledChange: ScheduledChange | null;
    /**
     * R12: which coupons the live subscription carries right now — how the
     * page knows a credit is still pending (a `once` coupon leaves the
     * subscription once an invoice used it). Null when Stripe is off, there
     * is no subscription, or the read failed.
     */
    discounts: { agreed: boolean; credit: boolean; percent: { endsAt: string | null } | null } | null;
    /** PR 2: a Stripe schedule steers the subscription */
    scheduled?: boolean;
    /** PR 2: the CUSTOMER cancelled — when it ends; nothing here may undo it */
    cancelsAt?: string | null;
  };
  /** R12: the account's earliest active owner-role login, for the page head */
  owner: { email: string; name: string | null } | null;
  /** R12: monthly-equivalent MRR after the agreed price and a running percent-off, and the plan's list price */
  mrr: { cents: number; listCents: number };
  /** PR 2: the terms waiting for a later phase, if any */
  pendingTerms?: PendingTerms | null;
}

/** The Change plan dialog's answer (PR 2): what the one change did. */
export interface BillingChangeResult {
  success: boolean;
  via: 'stripe' | 'local';
  summary: string;
  emailed: boolean;
  pendingTermsId: number | null;
  scheduleId: string | null;
  freePeriod: 'started' | 'updated' | 'ended' | null;
  discountsEnded: { agreedCents: number | null; percent: number | null } | null;
  chargedNowCents: number | null;
  warning: string | null;
}

/** One change — plan, interval, price, a free period first, and when — in the handoff's names. */
export const putAccountBilling = (id: number, body: Record<string, unknown>) =>
  apiFetch<BillingChangeResult>(`/admin/platform/accounts/${id}/billing`, { method: 'PUT', body: JSON.stringify(body) });

/** Stripe's own figure for a change made now, prorated; null when the change is not one. */
export interface BillingQuote {
  immediate: boolean;
  amountCents: number;
  lines: { description: string; amountCents: number }[];
  at: string | null;
}
export const quoteAccountBilling = (id: number, body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; quote: BillingQuote | null }>(`/admin/platform/accounts/${id}/billing/quote`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const getAccountBilling = (id: number) =>
  apiFetch<AccountBillingView>(`/admin/platform/accounts/${id}/billing`);

/** Staff plan change — writes to Stripe and emails the tenant. */
export const setAccountPlan = (id: number, body: {
  planId: number; interval: 'month' | 'year'; effective: PlanChangeEffective;
  /** the agreed price in cents, null to return them to list, omitted to leave alone */
  priceOverrideCents?: number | null;
  priceOverrideNote?: string;
}) =>
  // `via` and `emailed` are the two facts the caller cannot assume: a plan
  // change lands locally when there is no live subscription to move, and the
  // confirmation email is silently skipped when SMTP is not configured. A
  // success message that claims both regardless is a message that lies.
  // `discounts` rides along because a new plan means a new list price, which
  // makes a standing agreed-price coupon the wrong size until it is re-struck.
  apiFetch<{
    success: boolean; via: 'stripe' | 'local'; emailed: boolean; discounts?: DiscountSync;
    /** what actually happened — every deferral collapses to 'now' with no subscription */
    appliedEffective: PlanChangeEffective;
    /**
     * One discount at a time (Ben, 2026-09-26): a change of plan or interval
     * ends the percent-off the moment it is made. The percent that ended, or
     * null (absent from an older server). A pending credit stays.
     */
    percentEnded?: { percent: number; months: number | null } | null;
    /**
     * The percent that ended is still on their Stripe subscription: the server
     * checked, and Stripe did not take it off. Never "has ended" then —
     * `percentStillOnBillReason` is the sentence staff read instead.
     */
    percentStillOnBill?: boolean;
    percentStillOnBillReason?: string | null;
  }>(
    `/admin/platform/accounts/${id}/plan`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );

/**
 * Issue or withdraw a one-time credit. `days: null` withdraws it; anything
 * else is 1..30, the ceiling that keeps a credit inside a single invoice.
 */
export const setAccountCredit = (id: number, body: { days: number | null; reason: string }) =>
  apiFetch<{ success: boolean; discounts: DiscountSync; emailed: boolean }>(
    `/admin/platform/accounts/${id}/credit`,
    { method: 'POST', body: JSON.stringify(body) },
  );

/** What the grant form posts. Dates are plain calendar days. */
export interface TrialGrantBody {
  planId: number;
  startsAt: string;
  expiresAt: string;
  cardRequired: boolean;
  autoConvert: boolean;
  notify: boolean;
  note: string;
}

/** Grant a free upgrade period. Resolves any active one as revoked. */
export const grantTrial = (id: number, body: TrialGrantBody) =>
  apiFetch<{ success: boolean; trial: AccountTrial; checkoutUrl?: string; note?: string }>(
    `/admin/platform/accounts/${id}/trials`, { method: 'POST', body: JSON.stringify(body) }
  );

/**
 * Extend (new expiry), or switch off the conversion charge. Stripe is written
 * only when it holds the period — a `trialing` subscription; a period that
 * stood locally over a paying subscription leaves that subscription alone,
 * and `stripe.updated: false` says so (absent from an older server).
 */
export const patchTrial = (id: number, trialId: number, body: { expiresAt: string } | { autoConvert: false }) =>
  apiFetch<{ success: boolean; trial: AccountTrial; stripe?: { updated: boolean; reason: string | null } }>(
    `/admin/platform/accounts/${id}/trials/${trialId}`, { method: 'PATCH', body: JSON.stringify(body) }
  );

/** Revoke now — drops the account back to its paid plan immediately. */
export const revokeTrial = (id: number, trialId: number, resolutionNote: string) =>
  apiFetch<{ success: boolean; trial?: AccountTrial; stripe?: { updated: boolean } }>(`/admin/platform/accounts/${id}/trials/${trialId}`, {
    method: 'DELETE', body: JSON.stringify({ resolutionNote }),
  });

/** Mint a Billing Portal link and email it — staff never see the card. */
export const sendPortalLink = (id: number) =>
  apiFetch<{ success: boolean; emailedTo?: string }>(`/admin/platform/accounts/${id}/portal-link`, {
    method: 'POST',
  });

/**
 * Start or replace the running percent-off (D4). It rides on the Stripe
 * subscription beside the agreed-price and credit coupons. 400 with a
 * sentence for a bad percent or duration; 409 with no live subscription to
 * attach it to; 502 when Stripe refused, in which case nothing changed.
 * With Stripe off it stays local and `discounts.reason` says so.
 */
export const setAccountDiscount = (id: number, body: { percent: number; months: PercentMonths | null; note?: string }) =>
  apiFetch<{ success: boolean; discounts: DiscountSync }>(`/admin/platform/accounts/${id}/discount`, {
    method: 'POST', body: JSON.stringify(body),
  });

/** End the percent-off now. 404 when there is none. */
export const removeAccountDiscount = (id: number) =>
  apiFetch<{ success: boolean; discounts: DiscountSync }>(`/admin/platform/accounts/${id}/discount`, {
    method: 'DELETE',
  });

/**
 * This account's billing trail (plan, price, free periods, credits, billing
 * email, portal links, discounts), newest first, from the platform audit.
 * `before` is the previous page's `nextCursor`.
 */
export const getAccountHistory = (id: number, opts: { limit?: number; before?: string | null } = {}) => {
  const q = new URLSearchParams();
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.before) q.set('before', opts.before);
  const qs = q.toString();
  return apiFetch<{ success: boolean; entries: PlatformAuditEntry[]; nextCursor: string | null }>(
    `/admin/platform/accounts/${id}/history${qs ? `?${qs}` : ''}`,
  );
};

// ---------------------------------------------------------------------------
// Public-site content: the release history and FAQ the marketing site shows.
// Reads are any staff member's; writes need the Super Admin role.
// ---------------------------------------------------------------------------

export interface SiteRelease {
  id: number;
  version: string;
  title: string;
  /** YYYY-MM-DD */
  released_on: string;
  summary: string;
  highlights: string[];
}

export type ReleaseBody = Omit<SiteRelease, 'id'>;

export const listSiteReleases = () =>
  apiFetch<{ success: boolean; releases: SiteRelease[] }>('/admin/platform/site-content/releases');

export const createSiteRelease = (body: ReleaseBody) =>
  apiFetch<{ success: boolean; release: SiteRelease }>('/admin/platform/site-content/releases', {
    method: 'POST', body: JSON.stringify(body),
  });

export const saveSiteRelease = (id: number, body: ReleaseBody) =>
  apiFetch<{ success: boolean; release: SiteRelease }>(`/admin/platform/site-content/releases/${id}`, {
    method: 'PUT', body: JSON.stringify(body),
  });

export const deleteSiteRelease = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/platform/site-content/releases/${id}`, { method: 'DELETE' });

export interface SiteFaqEntry {
  id: number;
  category: string;
  question: string;
  /** plain text; blank line = new paragraph, [label](url) = link */
  answer: string;
  sort_order: number;
  published: boolean;
  updated_at: string;
}

export type FaqBody = Omit<SiteFaqEntry, 'id' | 'updated_at'>;

export const listSiteFaq = () =>
  apiFetch<{ success: boolean; faq: SiteFaqEntry[] }>('/admin/platform/site-content/faq');

export const createSiteFaq = (body: FaqBody) =>
  apiFetch<{ success: boolean; entry: SiteFaqEntry }>('/admin/platform/site-content/faq', {
    method: 'POST', body: JSON.stringify(body),
  });

export const saveSiteFaq = (id: number, body: FaqBody) =>
  apiFetch<{ success: boolean; entry: SiteFaqEntry }>(`/admin/platform/site-content/faq/${id}`, {
    method: 'PUT', body: JSON.stringify(body),
  });

export const deleteSiteFaq = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/platform/site-content/faq/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// The site document, the screenshot library and the favicon (078): the look
// and the Home and Features copy wait for Publish; a screenshot is live when
// it is saved. Reads are any staff member's; writes need the Super Admin role.
// ---------------------------------------------------------------------------

/** The document as the console holds it: what is live, and the shared draft (if any). */
export interface SiteDocState {
  published: SiteDoc;
  publishedAt: string | null;
  publishedBy: string | null;
  draft: SiteDoc | null;
  /** The draft's stamp: every save, publish and discard echoes it back as `base`. */
  draftAt: string | null;
  draftBy: string | null;
  /** The size and type of the favicons the two documents name. */
  favicons: Record<string, { width: number; height: number; type: string }>;
}

export const getSiteDoc = () =>
  apiFetch<{ success: boolean } & SiteDocState>('/admin/platform/site-content/site');

/** Save the shared draft. A 409 (`draft_conflict`) carries the draft somebody else saved since. */
export const saveSiteDraft = (doc: SiteDoc, base: string | null) =>
  apiFetch<{ success: boolean; draft: SiteDoc | null; draftAt: string | null; draftBy: string | null }>(
    '/admin/platform/site-content/site/draft', { method: 'PUT', body: JSON.stringify({ doc, base }) }
  );

export const publishSiteDoc = (doc: SiteDoc, base: string | null) =>
  apiFetch<{ success: boolean; published: SiteDoc; publishedAt: string | null; publishedBy: string | null }>(
    '/admin/platform/site-content/site/publish', { method: 'POST', body: JSON.stringify({ doc, base }) }
  );

export const discardSiteDraft = (base: string | null) =>
  apiFetch<{ success: boolean }>(
    `/admin/platform/site-content/site/draft${base ? `?base=${encodeURIComponent(base)}` : ''}`, { method: 'DELETE' }
  );

/** A library entry as the console reads it: who saved it last, and when. */
export interface SiteShotEntry extends SiteShot {
  updatedAt: string | null;
  updatedBy: string | null;
}

export const listSiteShots = () =>
  apiFetch<{ success: boolean; shots: SiteShotEntry[] }>('/admin/platform/site-content/shots');

/** What a screenshot save sends: the words, and whichever images changed. */
export interface SiteShotUpload {
  alt: string;
  url: string;
  /** a new shot's name, "payments.png" (ignored on an existing one) */
  file?: string;
  desktop?: File | null;
  phone?: File | null;
  removePhone?: boolean;
}

const shotForm = (u: SiteShotUpload) => {
  const form = new FormData();
  form.append('alt', u.alt);
  form.append('url', u.url);
  if (u.file !== undefined) form.append('file', u.file);
  if (u.desktop) form.append('desktop', u.desktop);
  if (u.phone) form.append('phone', u.phone);
  if (u.removePhone) form.append('removePhone', '1');
  return form;
};

export const createSiteShot = (u: SiteShotUpload) =>
  apiFetch<{ success: boolean; shot: SiteShotEntry }>('/admin/platform/site-content/shots', {
    method: 'POST', body: shotForm(u),
  });

export const saveSiteShot = (id: string, u: SiteShotUpload) =>
  apiFetch<{ success: boolean; shot: SiteShotEntry }>(`/admin/platform/site-content/shots/${encodeURIComponent(id)}`, {
    method: 'PUT', body: shotForm(u),
  });

/** Upload a favicon for the draft; the answer's `name` is what the draft's look.favicon takes. */
export const uploadSiteFavicon = (file: File) => {
  const form = new FormData();
  form.append('favicon', file);
  return apiFetch<{ success: boolean; favicon: { name: string; width: number; height: number; type: string } }>(
    '/admin/platform/site-content/favicon', { method: 'POST', body: form }
  );
};
